import { DateTime } from 'luxon';
import {
  array,
  date,
  number,
  object,
  SchemaOf,
  string,
  StringSchema,
  MixedSchema,
  mixed
} from 'yup';
import {
  TimeSignalClassification,
  LabelType,
  LabelValueSource,
  LabelValueAcceptedBy
} from 'graphql/types';
import { validateDuration } from '../common/utils';
import { SplitEntry } from './types';

export const COMMENT_MAX_CHAR_LIMIT = 300;
export const NOTE_MAX_CHAR_LIMIT = 2000;

export type SplitEntryFormData = SplitEntry;

export type SplitNoteFormData = {
  splitEntries: SplitEntryFormData[];
  classification: TimeSignalClassification;
};

export const splitNoteSchema = (
  isDurationIndipendant?: boolean
): SchemaOf<SplitNoteFormData> => {
  return object().shape({
    classification: mixed<TimeSignalClassification>().optional(),
    splitEntries: array()
      .of(
        object().shape(
          {
            id: string().required(),
            description: string().max(
              NOTE_MAX_CHAR_LIMIT,
              'note.error.maxCommentLength'
            ),
            date: date().notRequired().nullable(),
            startTime: date()
              .notRequired()
              .nullable()
              .when('endTime', {
                is: (val: Date | null) => {
                  return Boolean(val);
                },
                then: date().nullable().required('note.error.startTimeRequired')
              }),
            endTime: date()
              .notRequired()
              .nullable()
              .when('startTime', {
                is: (val: Date | null) => Boolean(val),
                then: date()
                  .nullable()
                  .required('note.error.endTimeRequired')
                  .when(['startTime', 'endTime'], {
                    is: (startTime: Date, endTime: Date) => startTime > endTime,
                    then: date()
                      .nullable()
                      .test(
                        'startTime_endTime_validation',
                        'note.error.endTimeShouldBeGreaterThanStartTime',
                        () => false
                      ),
                    otherwise: date()
                      .nullable()
                      .when(['startTime', 'endTime'], {
                        is: (startTime: Date, endTime: Date) => {
                          const start = DateTime.fromJSDate(startTime);
                          const end = DateTime.fromJSDate(endTime);
                          return start.toISO() === end.toISO();
                        },
                        then: date()
                          .nullable()
                          .test(
                            'startTime_endTime_validation',
                            'note.error.startAndEndTimeShouldNotBeEqual',
                            () => false
                          )
                      })
                  })
              }),
            duration: number()
              .typeError('note.error.durationRequired')
              .notRequired()
              .min(0)
              .max(86400)
              .when('startTime', {
                is: (val: Date | null) => Boolean(val),
                then: number().required('note.error.durationRequired')
              })
              .when('endTime', {
                is: (val: Date | null) => Boolean(val),
                then: number().required('note.error.durationRequired')
              })
              .when(['startTime', 'endTime'], {
                is: (startTime: Date, endTime: Date) =>
                  isDurationIndipendant &&
                  Boolean(startTime) &&
                  Boolean(endTime),
                then: number().when(['startTime', 'endTime', 'duration'], {
                  is: (startTime: Date, endTime: Date, duration: number) => {
                    const start = DateTime.fromJSDate(startTime);
                    const end = DateTime.fromJSDate(endTime);

                    const isDurationValid = validateDuration(
                      start,
                      end,
                      duration
                    );
                    return isDurationValid;
                  },
                  then: number()
                    .required()
                    .test(
                      'duration_validation',
                      'note.error.durationShouldBeInStartTimeEndTimeRange',
                      () => false
                    )
                })
              }),
            timesheetLabels: array()
              .notRequired()
              .of(
                object().shape({
                  label: object().shape({
                    // Using the base constructors is a hack required to generate the correct types.
                    // Appears to be related to an open issue with yup: https://github.com/jquense/yup/issues/1247.
                    id: new StringSchema<string>().required(),
                    type: new MixedSchema<LabelType>()
                      .oneOf(Object.values(LabelType))
                      .required(),
                    name: new StringSchema<string>().required(),
                    defaultValue: object()
                      .notRequired()
                      .nullable()
                      .shape({
                        idValue: string().notRequired().nullable(),
                        stringValue: string().notRequired().nullable(),
                        numericValue: number().notRequired().nullable()
                      })
                      .default(undefined),
                    value: object().when('type', (type: LabelType, schema) => {
                      if (type === LabelType.Comment) {
                        return schema
                          .notRequired()
                          .nullable()
                          .shape({
                            idValue: string().notRequired().nullable(),
                            stringValue: string()
                              .notRequired()
                              .nullable()
                              .max(COMMENT_MAX_CHAR_LIMIT),
                            numericValue: number().notRequired().nullable()
                          });
                      }

                      return schema.notRequired().nullable().shape({
                        idValue: string().notRequired().nullable(),
                        stringValue: string().notRequired().nullable(),
                        numericValue: number().notRequired().nullable()
                      });
                    }),
                    valueSource: new StringSchema<LabelValueSource>()
                      .notRequired()
                      .nullable(),
                    acceptedBy: new StringSchema<LabelValueAcceptedBy>()
                      .notRequired()
                      .nullable(),
                    suggestedValues: array()
                      .notRequired()
                      .nullable()
                      .of(
                        object().notRequired().nullable().shape({
                          idValue: string().notRequired().nullable(),
                          stringValue: string().notRequired().nullable(),
                          numericValue: number().notRequired().nullable()
                        })
                      )
                  })
                })
              ),
            customLabels: array()
              .notRequired()
              .nullable()
              .of(
                object().shape({
                  id: new StringSchema<string>().required(),
                  type: new MixedSchema<LabelType>()
                    .oneOf(Object.values(LabelType))
                    .required(),
                  name: new StringSchema<string>().required(),
                  value: object().when('type', (type: LabelType, schema) => {
                    return schema.notRequired().nullable().shape({
                      idValue: string().notRequired().nullable(),
                      stringValue: string().notRequired().nullable(),
                      numericValue: number().notRequired().nullable()
                    });
                  }),
                  valueSource: new StringSchema<LabelValueSource>()
                    .notRequired()
                    .nullable(),
                  acceptedBy: new StringSchema<LabelValueAcceptedBy>()
                    .notRequired()
                    .nullable(),
                  suggestedValues: array()
                    .notRequired()
                    .nullable()
                    .of(
                      object().notRequired().nullable().shape({
                        idValue: string().notRequired().nullable(),
                        stringValue: string().notRequired().nullable(),
                        numericValue: number().notRequired().nullable()
                      })
                    )
                })
              )
          },
          [['startTime', 'endTime']]
        )
      )
      .notRequired()
      .nullable()
  });
};
